import { XCircle } from 'lucide-react';
import { useT } from '@/i18n/provider';
import { cn } from '@/lib/utils';

interface PainPoint {
  key: string;
  variant: 'default' | 'lighter' | 'highlight';
  content: (t: (key: string) => string) => React.ReactNode;
}

const painPoints: PainPoint[] = [
  {
    key: 'grep',
    variant: 'default',
    content: (t) => (
      <>
        {t('problem.grep.before')}
        <strong className="text-gray-900 dark:text-white font-semibold">{t('problem.grep.bold')}</strong>
        {t('problem.grep.after')}
      </>
    ),
  },
  {
    key: 'tools',
    variant: 'lighter',
    content: (t) => (
      <>
        {t('problem.tools.before')}
        <strong className="text-gray-900 dark:text-white font-semibold">{t('problem.tools.bold')}</strong>
        {t('problem.tools.after')}
        <strong className="text-gray-900 dark:text-white font-semibold">{t('problem.tools.bold2')}</strong>
        {t('problem.tools.after2')}
      </>
    ),
  },
  {
    key: 'alerts',
    variant: 'highlight',
    content: (t) => (
      <>
        {t('problem.alerts.before')}
        <strong className="text-gray-900 dark:text-white font-semibold">{t('problem.alerts.bold')}</strong>
        {t('problem.alerts.after')}
        <strong className="text-gray-900 dark:text-white font-semibold">{t('problem.alerts.bold2')}</strong>
        {t('problem.alerts.after2')}
      </>
    ),
  },
  {
    key: 'costs',
    variant: 'lighter',
    content: (t) => (
      <>
        {t('problem.costs.before')}
        <strong className="text-gray-900 dark:text-white font-semibold">{t('problem.costs.bold')}</strong>
        {t('problem.costs.after')}
      </>
    ),
  },
  {
    key: 'compliance',
    variant: 'default',
    content: (t) => (
      <>
        {t('problem.compliance.before')}
        <strong className="text-gray-900 dark:text-white font-semibold">{t('problem.compliance.bold')}</strong>
        {t('problem.compliance.after')}
        <strong className="text-gray-900 dark:text-white font-semibold">{t('problem.compliance.bold2')}</strong>
        {t('problem.compliance.after2')}
      </>
    ),
  },
];

const variantStyles: Record<PainPoint['variant'], string> = {
  default:
    'bg-red-50 dark:bg-gray-900/40 border-l-4 border-l-destructive-500/60 border-y border-r border-y-red-200/60 dark:border-y-gray-800/40 border-r-red-200/60 dark:border-r-gray-800/40 shadow-sm shadow-red-100/50 dark:shadow-none',
  lighter:
    'bg-white dark:bg-gray-800/30 border-l-4 border-l-destructive-500/40 border-y border-r border-y-red-200/50 dark:border-y-gray-700/30 border-r-red-200/50 dark:border-r-gray-700/30 shadow-sm shadow-red-100/40 dark:shadow-none',
  highlight:
    'bg-gradient-to-r from-red-50 via-red-50/60 to-white dark:from-destructive-500/10 dark:via-destructive-500/5 dark:to-transparent border-l-4 border-l-destructive-500 border-y border-r border-y-red-200/70 dark:border-y-destructive-500/15 border-r-red-200/60 dark:border-r-destructive-500/10 shadow-md shadow-red-100/60 dark:shadow-[0_0_30px_-8px_rgba(239,68,68,0.15)]',
};

export function ProblemSection() {
  const t = useT();

  return (
    <section className="pt-20 pb-16 sm:pt-24 sm:pb-20" aria-labelledby="problem-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-14 sm:mb-16 animate-on-scroll">
            <h2
              id="problem-heading"
              className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 dark:text-white"
            >
              <span className="relative inline-block">
                {t('problem.title')}
                <span className="absolute -bottom-1.5 left-0 right-0 h-[3px] bg-gradient-to-r from-destructive-500 via-warning-500 to-destructive-500/0 rounded-full" />
              </span>
            </h2>
            <p className="mt-4 text-lg text-gray-500 dark:text-gray-400 italic">
              {t('problem.subtitle')}
            </p>
          </div>

          {/* Pain points */}
          <ul className="space-y-5 sm:space-y-6 mb-12 sm:mb-14" role="list">
            {painPoints.map((point, i) => (
              <li
                key={point.key}
                className={cn(
                  'animate-on-scroll group rounded-xl px-6 sm:px-8 py-5 sm:py-6 flex items-start gap-4',
                  'transition-all duration-200 ease-out',
                  'hover:scale-[1.02] hover:shadow-lg hover:shadow-destructive-500/5',
                  'focus-within:scale-[1.02] focus-within:ring-2 focus-within:ring-destructive-500/30 focus-within:outline-none',
                  variantStyles[point.variant],
                )}
                style={{ transitionDelay: `${i * 80}ms` }}
                tabIndex={0}
                aria-label={t(`problem.${point.key}.aria`)}
              >
                {/* Icon */}
                <div className="relative shrink-0 mt-0.5">
                  <XCircle
                    className="h-6 w-6 text-destructive-500 drop-shadow-[0_0_6px_rgba(239,68,68,0.4)]"
                    aria-hidden="true"
                  />
                </div>

                {/* Content */}
                <p className={cn(
                  'text-gray-600 dark:text-gray-400 leading-relaxed',
                  point.variant === 'highlight' ? 'text-sm sm:text-base' : 'text-sm sm:text-[15px]',
                )}>
                  {point.content(t)}
                </p>
              </li>
            ))}
          </ul>

          {/* Punchline */}
          <div className="animate-on-scroll text-center">
            <p className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white leading-snug">
              {t('problem.punchline')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
